import type { ITextReplacement, RangeResolutionOutcome } from "../global";
import { findRange } from "./Edit";

/**
 * Replaces text at a specified paragraph/segment location and selects the result.
 * Throws and skips document mutation unless the segment resolves exactly.
 */
export async function replaceText(
  context: Word.RequestContext,
  detail: ITextReplacement
): Promise<void> {
  let result: Awaited<ReturnType<typeof findRange>>;
  try {
    result = await findRange(context, {
      paragraph: detail.paragraph,
      segment: detail.segment,
    });
  } catch (error: unknown) {
    const skippedError = createSkippedReplacementError(
      detail,
      "lookup-failed",
      error
    );
    console.error(skippedError);
    throw skippedError;
  }

  if (result.outcome !== "segment") {
    const skippedError = createSkippedReplacementError(
      detail,
      result.outcome,
      result.outcome === "paragraph-fallback" ? result.failure.error : undefined
    );
    console.error(skippedError);
    throw skippedError;
  }

  result.range.insertText(detail.replacement, "Replace");
  result.range.select("Select");
  await context.sync();
}

function createSkippedReplacementError(
  detail: ITextReplacement,
  outcome: RangeResolutionOutcome | "lookup-failed",
  cause?: unknown
): Error {
  const message = `Skipped text replacement: ${outcome}; target=${formatReplacementTarget(detail)}`;
  return cause === undefined
    ? new Error(message)
    : new Error(message, { cause });
}

function formatReplacementTarget(detail: ITextReplacement): string {
  return JSON.stringify({
    paragraph: {
      query: detail.paragraph.query,
      index: detail.paragraph.index,
      offset: detail.paragraph.offset,
    },
    segment: {
      query: detail.segment.query,
      index: detail.segment.index,
      offset: detail.segment.offset,
    },
  });
}

